/** HMO portal functional roles ({@code roles.role} in DB). Legacy IHX roles removed. */
export const HMO_ROLE = {
  RI: "HMO_RI",
  CPD: "HMO_CPD",
  APPROVER: "HMO_APPROVER",
  AUDITOR: "HMO_AUDITOR",
  SPECIALIST: "HMO_SPECIALIST",
  DIRECTOR: "HMO_DIRECTOR",
  CMD: "HMO_CMD",
  FINANCE: "HMO_FINANCE",
  ADMIN: "HMO_ADMIN",
  ENHANCEMENT_ADMIN: "HMO_ENHANCEMENT_ADMIN",
  DOCTOR: "HMO_DOCTOR",
  HOD: "HMO_HOD",
  HSP_USER: "HSP_USER",
  HMO_PORTAL_USER: "HMO_PORTAL_USER",
} as const;

export type HmoRoleCode = (typeof HMO_ROLE)[keyof typeof HMO_ROLE];

export const normalizeHmoRoleCode = (role?: string | null): string =>
  String(role || "").trim().toUpperCase();

export const HMO_AUTHORITY_ROLE_CODES: HmoRoleCode[] = [
  HMO_ROLE.RI,
  HMO_ROLE.CPD,
  HMO_ROLE.APPROVER,
  HMO_ROLE.AUDITOR,
  HMO_ROLE.SPECIALIST,
  HMO_ROLE.DIRECTOR,
  HMO_ROLE.CMD,
  HMO_ROLE.FINANCE,
  HMO_ROLE.ADMIN,
  HMO_ROLE.ENHANCEMENT_ADMIN,
  HMO_ROLE.DOCTOR,
  HMO_ROLE.HOD,
];

export const HMO_ROLE_LABELS: Record<string, string> = {
  [HMO_ROLE.RI]: "Referral Issuer",
  [HMO_ROLE.CPD]: "CPD / Reviewer",
  [HMO_ROLE.APPROVER]: "Approver",
  [HMO_ROLE.AUDITOR]: "Auditor",
  [HMO_ROLE.SPECIALIST]: "Expert",
  [HMO_ROLE.DIRECTOR]: "Director",
  [HMO_ROLE.CMD]: "CMO",
  [HMO_ROLE.FINANCE]: "Finance",
  [HMO_ROLE.ADMIN]: "Admin",
  [HMO_ROLE.ENHANCEMENT_ADMIN]: "Enhancement Admin",
  [HMO_ROLE.DOCTOR]: "Doctor",
  [HMO_ROLE.HOD]: "HOD",
  [HMO_ROLE.HSP_USER]: "Hospital User",
};

export const HMO_ROLE_ABBREVIATIONS: Record<string, string> = {
  [HMO_ROLE.RI]: "RI",
  [HMO_ROLE.CPD]: "CPD",
  [HMO_ROLE.APPROVER]: "APR",
  [HMO_ROLE.AUDITOR]: "AUD",
  [HMO_ROLE.SPECIALIST]: "EXP",
  [HMO_ROLE.DIRECTOR]: "DIR",
  [HMO_ROLE.CMD]: "CMO",
  [HMO_ROLE.FINANCE]: "FIN",
  [HMO_ROLE.ADMIN]: "ADM",
  [HMO_ROLE.ENHANCEMENT_ADMIN]: "ENA",
  [HMO_ROLE.DOCTOR]: "DOC",
  [HMO_ROLE.HOD]: "HOD",
  [HMO_ROLE.HSP_USER]: "HSP",
};

export const getHmoRoleLabel = (role?: string | null, isHospital?: boolean): string => {
  if (isHospital) {
    return HMO_ROLE_LABELS[HMO_ROLE.HSP_USER] ?? "Hospital User";
  }
  if (!role) return "";
  return HMO_ROLE_LABELS[role] ?? role;
};

export const getHmoRoleAbbreviation = (role?: string | null): string => {
  if (!role) return "";
  return HMO_ROLE_ABBREVIATIONS[role] ?? role.slice(0, 3).toUpperCase();
};

export const isHmoAuthorityRole = (role?: string | null): boolean => {
  const normalized = normalizeHmoRoleCode(role);
  return !!normalized && HMO_AUTHORITY_ROLE_CODES.includes(normalized as HmoRoleCode);
};

export const HMO_WORKFLOW_REVIEWER_ROLE_CODES: HmoRoleCode[] = [
  HMO_ROLE.CPD,
  HMO_ROLE.APPROVER,
  HMO_ROLE.AUDITOR,
  HMO_ROLE.SPECIALIST,
  HMO_ROLE.CMD,
  HMO_ROLE.FINANCE,
];

export const isHmoCpd = (role?: string | null): boolean => role === HMO_ROLE.CPD;

/** CPD, Approver, Auditor, and CMD use claim workflow sub-tabs on the Claims list tab. */
export const isHmoReviewerAuthorityTabUser = (role?: string | null): boolean => {
  const normalized = normalizeHmoRoleCode(role);
  if (!normalized) return false;
  return (
    normalized === HMO_ROLE.CPD ||
    normalized === HMO_ROLE.APPROVER ||
    normalized === HMO_ROLE.AUDITOR ||
    normalized === HMO_ROLE.CMD
  );
};

/** Auditor, Approver, and CMD see the full authority patient-list tab set. */
export const isHmoFullAuthorityTabUser = (role?: string | null): boolean => {
  const normalized = normalizeHmoRoleCode(role);
  if (!normalized) return false;
  return (
    normalized === HMO_ROLE.APPROVER ||
    normalized === HMO_ROLE.AUDITOR ||
    normalized === HMO_ROLE.CMD
  );
};

/** @deprecated Use isHmoCpd or isHmoReviewerAuthorityTabUser instead. */
export const isHmoClaimOnlyTabUser = (role?: string | null): boolean =>
  isHmoCpd(role);

/** Workflow reviewers (not CPD) show tab amounts on the authority summary tabs. */
export const showAuthorityTabAmounts = (role?: string | null): boolean =>
  !isHmoCpd(role);

export const isHmoEnhancementAdmin = (role?: string | null): boolean =>
  normalizeHmoRoleCode(role) === HMO_ROLE.ENHANCEMENT_ADMIN;

/** CPD, Approver, Enhancement Admin, Doctor, HOD, and Admin may approve / raise NMI on enhancements. */
export const canActOnBpaEnhancement = (role?: string | null): boolean =>
  role === HMO_ROLE.APPROVER ||
  isHmoDoctorFamily(role) ||
  isHmoAdmin(role) ||
  isHmoEnhancementAdmin(role);

/** CPD-only workflow patient list (submitted / query-replied claims). */
export const isHmoCpdWorkflowListUser = (role?: string | null): boolean =>
  isHmoCpd(role);

/** Expert workflow list — seek opinion cases only. */
export const isHmoExpertWorkflowListUser = (role?: string | null): boolean =>
  role === HMO_ROLE.SPECIALIST;

export const isHmoWorkflowReviewer = (role?: string | null): boolean => {
  const normalized = normalizeHmoRoleCode(role);
  if (!normalized) return false;
  if (normalized === HMO_ROLE.ADMIN || normalized === "ADMIN") return true;
  return HMO_WORKFLOW_REVIEWER_ROLE_CODES.includes(normalized as HmoRoleCode);
};

export const isHmoCmdAdmin = (role?: string | null): boolean => role === HMO_ROLE.CMD || role === HMO_ROLE.ADMIN;
export const isHmoCmd = (role?: string | null): boolean => role === HMO_ROLE.CMD;

export const isHmoDoctor = (role?: string | null): boolean =>
  normalizeHmoRoleCode(role) === HMO_ROLE.DOCTOR;

export const isHmoHod = (role?: string | null): boolean =>
  normalizeHmoRoleCode(role) === HMO_ROLE.HOD;

/** Doctor and HOD share clinical review tabs / actions. */
export const isHmoDoctorFamily = (role?: string | null): boolean =>
  isHmoDoctor(role) || isHmoHod(role);

export const isHmoReferralIssuer = (role?: string | null): boolean =>
  normalizeHmoRoleCode(role) === HMO_ROLE.RI;

export const isHmoAdmin = (role?: string | null): boolean => {
  const normalized = String(role || "").trim().toUpperCase();
  return normalized === HMO_ROLE.ADMIN || normalized === "ADMIN";
};

export const showAdminPatientListSubFilters = (
  isHospital: boolean,
  roleCode?: string | null,
): boolean => isHospital || isHmoAdmin(roleCode) || isHmoCmdAdmin(roleCode);

/** Referral creation is limited to Referral Issuer and Admin roles. */
export const canCreateReferral = (role?: string | null): boolean =>
  isHmoReferralIssuer(role) || isHmoAdmin(role);

/** Patient list only: HMO users must assign a case before opening visit details from the list (not Hospital, RI, or Admin). */
export const isHmoCaseAssignmentRequired = (
  role?: string | null,
  isHospital?: boolean,
): boolean => {
  if (isHospital) return false;
  if (isHmoReferralIssuer(role)) return false;
  if (isHmoAdmin(role)) return false;
  if (isHmoEnhancementAdmin(role)) return false;
  if (isHmoDoctorFamily(role)) return false;
  return isHmoAuthorityRole(role);
};
